import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash } from "@phosphor-icons/react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

export default function CustomerDispositions() {
  const [systemDispos, setSystemDispos] = useState([]);
  const [dialerDispos, setDialerDispos] = useState([]);
  const [customDispos, setCustomDispos] = useState([]);
  const [dispoForm, setDispoForm] = useState({ code: "", label: "" });
  const [dispoSaving, setDispoSaving] = useState(false);
  const [dispoLoading, setDispoLoading] = useState(true);

  const loadDispositions = useCallback(async () => {
    setDispoLoading(true);
    try {
      const { data } = await api.get("/customer-settings/dispositions");
      setSystemDispos(data.system || []);
      setDialerDispos(data.dialer || []);
      setCustomDispos(data.custom || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setDispoLoading(false);
    }
  }, []);

  useEffect(() => { loadDispositions(); }, [loadDispositions]);

  const addDisposition = async () => {
    const code = (dispoForm.code || "").trim();
    const label = (dispoForm.label || "").trim();
    if (!code || !label) {
      toast.error("Code and label are required");
      return;
    }
    setDispoSaving(true);
    try {
      const { data } = await api.post("/customer-settings/dispositions", { code, label });
      setCustomDispos((rows) => [...rows, data].sort((a, b) => a.code.localeCompare(b.code)));
      setDispoForm({ code: "", label: "" });
      toast.success(`Disposition ${data.code} added`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setDispoSaving(false);
    }
  };

  const deleteDisposition = async (row) => {
    if (!row?.id || !row.deletable) return;
    setDispoSaving(true);
    try {
      await api.delete(`/customer-settings/dispositions/${row.id}`);
      setCustomDispos((rows) => rows.filter((r) => r.id !== row.id));
      toast.success(`Disposition ${row.code} removed`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setDispoSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="customer-dispositions-page">
      <PageHeader
        title="Disposition"
        subtitle="Agent wrap-up codes are selectable (including Answering Machine / AA). Dialer codes (DROP, PDROP, …) are set by the system from call evidence. Custom codes can be added."
      />

      <div className="rounded-xl border border-border bg-card p-5 space-y-5" data-testid="cust-dispositions-panel">
        <div className="rounded-lg border border-border overflow-hidden max-w-3xl">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[110px]">Type</TableHead>
                <TableHead className="w-[140px]">Code</TableHead>
                <TableHead>Label</TableHead>
                <TableHead className="w-[72px] text-right"> </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {dispoLoading && (
                <TableRow>
                  <TableCell colSpan={4} className="text-sm text-muted-foreground">Loading…</TableCell>
                </TableRow>
              )}
              {!dispoLoading && systemDispos.map((row) => (
                <TableRow key={`sys-${row.code}`} data-testid={`dispo-row-system-${row.code}`}>
                  <TableCell>
                    <span className="inline-block rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                      Agent
                    </span>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{row.code}</TableCell>
                  <TableCell className="text-sm">{row.label}</TableCell>
                  <TableCell className="text-right text-xs text-muted-foreground">—</TableCell>
                </TableRow>
              ))}
              {!dispoLoading && dialerDispos.map((row) => (
                <TableRow key={`dialer-${row.code}`} data-testid={`dispo-row-dialer-${row.code}`}>
                  <TableCell>
                    <span className="inline-block rounded-full bg-amber-50 text-amber-900 border border-amber-200 px-2 py-0.5 text-[11px] font-medium">
                      Dialer
                    </span>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{row.code}</TableCell>
                  <TableCell className="text-sm">
                    {row.label}
                    {row.notes ? (
                      <span className="block text-[11px] text-muted-foreground font-normal">{row.notes}</span>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-right text-xs text-muted-foreground">—</TableCell>
                </TableRow>
              ))}
              {!dispoLoading && customDispos.map((row) => (
                <TableRow key={row.id} data-testid={`dispo-row-custom-${row.code}`}>
                  <TableCell>
                    <span className="inline-block rounded-full bg-teal-50 text-teal-800 border border-teal-200 px-2 py-0.5 text-[11px] font-medium">
                      Custom
                    </span>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{row.code}</TableCell>
                  <TableCell className="text-sm">{row.label}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="rounded-lg px-2 text-rose-600 hover:text-rose-700"
                      disabled={dispoSaving}
                      onClick={() => deleteDisposition(row)}
                      data-testid={`dispo-delete-${row.code}`}
                      title="Delete custom disposition"
                    >
                      <Trash className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!dispoLoading && !systemDispos.length && !dialerDispos.length && !customDispos.length && (
                <TableRow>
                  <TableCell colSpan={4} className="text-sm text-muted-foreground">No dispositions.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        <div className="max-w-2xl space-y-3 rounded-lg border border-dashed border-border p-4">
          <p className="text-sm font-medium">Add custom disposition</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Code</Label>
              <Input
                value={dispoForm.code}
                onChange={(e) => setDispoForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                placeholder="e.g. FOLLOWUP"
                maxLength={32}
                className="font-mono"
                data-testid="dispo-new-code"
                autoComplete="off"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Label</Label>
              <Input
                value={dispoForm.label}
                onChange={(e) => setDispoForm((f) => ({ ...f, label: e.target.value }))}
                placeholder="e.g. Sale closed"
                maxLength={80}
                data-testid="dispo-new-label"
                autoComplete="off"
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Code: letters, numbers, underscore; must start with a letter. Cannot reuse a system code.
          </p>
          <Button
            className="rounded-lg"
            onClick={addDisposition}
            disabled={dispoSaving || !(dispoForm.code || "").trim() || !(dispoForm.label || "").trim()}
            data-testid="dispo-add-btn"
          >
            {dispoSaving ? "Saving…" : "Add disposition"}
          </Button>
        </div>
      </div>
    </div>
  );
}
